import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'

import { Incident } from './incident'
import { Phone } from './phone'
import { Rules } from './rules'
import { Card, CardTitle } from '../../components/card'
import { Page, PageHeader } from '../../components/page'
import { ScreenError, ScreenLoading } from '../../components/state'
import { useAlerts, useIncidents, useNotificationRules } from '../../app/queries'
import { T } from '../../copy/text'
import './alert.css'

/**
 * 屏 `alert`「出事的时候」。
 *
 * 桌面：规则 / 最近一次事故 / 手机 mockup。
 * ≤600px：只看告警列表和空态，不放装饰手机，也不做平板中间态。
 */
function useNarrowPhone(): boolean {
  const [narrow, setNarrow] = useState(false)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(max-width: 600px)')
    const apply = () => setNarrow(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])
  return narrow
}

export function AlertScreen() {
  const alerts = useAlerts()
  const rules = useNotificationRules()
  const incidents = useIncidents()
  const narrow = useNarrowPhone()

  if (alerts.isPending) return <ScreenLoading shape="split" />
  if (alerts.error) return <ScreenError error={alerts.error} />

  const items = [...(alerts.data?.items ?? [])].sort((a, b) =>
    b.raised_at.localeCompare(a.raised_at),
  )
  const latest = items[0]

  const latestIncident = [...(incidents.data?.items ?? [])].sort((a, b) =>
    b.started_at.localeCompare(a.started_at),
  )[0]

  return (
    <Page className="alert-theme">
      <PageHeader
        title={<T id="alert.page.title" />}
        subtitle={<T id="alert.page.subtitle" />}
        className="mb-npc-modal"
      />

      <p className="mb-npc-card mt-0 text-npc-body-sm">
        <Link to="/duty" className="text-npc-ink-medium underline-offset-2 hover:underline" data-testid="alert-duty-link">
          <T id="alert.duty.link" />
        </Link>
      </p>

      {narrow ? (
        <Card data-testid="alert-narrow-list">
          <CardTitle className="mb-npc-grid-sm block">
            <T id="alert.narrow.heading" />
          </CardTitle>
          {items.length === 0 ? (
            <p className="m-0 text-npc-body-sm text-npc-ink-soft" data-testid="alert-narrow-empty">
              <T id="alert.narrow.empty" />
            </p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-npc-grid-sm p-0">
              {items.map((alert) => (
                <li
                  key={alert.id}
                  data-testid="alert-narrow-row"
                  className="rounded-npc-card border border-npc-border bg-npc-panel p-npc-gap"
                >
                  <div className="text-npc-body-sm font-medium text-npc-ink-strong">{alert.title}</div>
                  <div className="mt-[4px] text-npc-meta text-npc-ink-softer">{alert.state}</div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-[1fr_372px] items-start gap-[26px] max-[1000px]:grid-cols-1">
          <div className="flex flex-col gap-npc-card-lg">
            {rules.data && rules.data.items.length > 0 ? (
              <Rules rules={rules.data.items} />
            ) : rules.data ? (
              <EmptyCard
                heading="alert.rules.heading"
                empty="alert.rules.empty"
                testId="alert-rules-empty"
              />
            ) : null}
            {latestIncident ? (
              <Incident incident={latestIncident} />
            ) : incidents.data ? (
              <EmptyCard
                heading="alert.incident.heading"
                empty="alert.incident.empty"
                testId="alert-incident-empty"
              />
            ) : null}
          </div>
          {latest ? (
            <div data-testid="alert-phone-mockup">
              <Phone alert={latest} now={latest.resolved_at ?? latest.raised_at} />
            </div>
          ) : (
            <EmptyCard
              heading="alert.phone.hint"
              empty="alert.phone.empty"
              testId="alert-phone-empty"
            />
          )}
        </div>
      )}
    </Page>
  )
}

function EmptyCard({
  heading,
  empty,
  testId,
}: {
  heading: 'alert.rules.heading' | 'alert.incident.heading' | 'alert.phone.hint'
  empty: 'alert.rules.empty' | 'alert.incident.empty' | 'alert.phone.empty'
  testId: 'alert-rules-empty' | 'alert-incident-empty' | 'alert-phone-empty'
}) {
  return (
    <Card>
      <CardTitle className="mb-npc-grid-sm block">
        <T id={heading} />
      </CardTitle>
      <p className="m-0 text-npc-body-sm text-npc-ink-soft" data-testid={testId}>
        <T id={empty} />
      </p>
    </Card>
  )
}
